import type { RootCause, SuppressionLevel } from "./_schema";

export type Tone = "stronger" | "softer";

/**
 * Verdict prose for the result page. Swappable per locale via
 * `deriveIgniteProfile(answers, { copy })`. Enum *labels* (SEVERE, CORTISOL
 * DOMINANCE, ...) are NOT here -- they are rendered by the widget and live in
 * web/src/i18n. The split follows where each is read from today.
 */
export type ProfileCopyPack = {
  assessmentLabel: Record<SuppressionLevel, string>;
  assessmentHeadline: Record<SuppressionLevel, string>;
  explanation: Record<RootCause, Record<Tone, string>>;
  fallbackExplanation: Record<Tone, string>;
};

export const EN_PROFILE_COPY: ProfileCopyPack = {
  assessmentLabel: {
    low: "STABLE",
    normal: "EARLY DECLINE",
    medium: "DECLINING",
    high: "SUPPRESSED",
  },
  assessmentHeadline: {
    low: "Your Testosterone Assessment: STABLE",
    normal: "Your Testosterone Assessment: EARLY DECLINE",
    medium: "Your Testosterone Assessment: DECLINING",
    high: "Your Testosterone Assessment: SUPPRESSED",
  },
  explanation: {
    "cortisol-dominance": {
      stronger:
        "Based on your responses, your testosterone production is being actively suppressed by chronic stress hormones.",
      softer:
        "Your responses show stress hormones starting to interfere with testosterone production.",
    },
    "chronic-stress": {
      stronger:
        "Sustained stress is overriding your hormonal recovery cycle. Your body is in defense mode, not rebuild mode.",
      softer: "Stress patterns are starting to shift your hormonal balance away from testosterone.",
    },
    "sleep-deprivation": {
      stronger:
        "Your sleep window is too short for the testosterone production cycle to complete. Most T release happens during deep sleep -- you are missing it.",
      softer: "Your sleep window is short enough to reduce nightly testosterone production.",
    },
    "visceral-adiposity": {
      stronger:
        "Body composition is converting your testosterone into estrogen via the aromatase pathway, creating a self-reinforcing loop.",
      softer:
        "Adipose tissue is starting to convert testosterone into estrogen, slowing your hormonal recovery.",
    },
    "metabolic-syndrome": {
      stronger:
        "Insulin and inflammatory signals are suppressing Leydig-cell testosterone production directly.",
      softer: "Metabolic markers are pointing toward early hormonal disruption.",
    },
    "lifestyle-load": {
      stronger:
        "Multiple lifestyle pressures are stacking on each other. There is no single fix -- your system needs a reset.",
      softer: "Several lifestyle inputs are pulling your hormonal output below where it should be.",
    },
    "age-related-decline": {
      stronger:
        "Age-related Leydig-cell decline is compounding with other suppressors. The drop is real, but the rate is not destiny.",
      softer:
        "Your body has begun the natural age-related decline. The slope is steeper than it needs to be.",
    },
    overtraining: {
      stronger: "Training load is outpacing recovery. Your testosterone-to-cortisol ratio is upside down.",
      softer:
        "Training load is starting to outpace recovery, suppressing testosterone signaling.",
    },
    "endocrine-disruption": {
      stronger:
        "Chemical exposure from processed food and packaging is creating a constant background load on your endocrine system.",
      softer:
        "Background endocrine-disruptor exposure is adding low-grade pressure to your hormonal system.",
    },
    "catabolic-deficit": {
      stronger:
        "Rapid weight loss is putting your body in crisis mode -- pulling testosterone down and breaking down muscle for fuel. The fat is leaving, but so is the strength underneath it.",
      softer:
        "Fast weight loss is starting to pull your testosterone down, so some of what you're losing is muscle, not just fat.",
    },
    "blocked-testosterone": {
      stronger:
        "Your testosterone isn't missing -- it's being locked away before your body can use it. SHBG binds what you produce, and too much is converted to estrogen first.",
      softer:
        "Your body is still making testosterone, but more of it is being bound and converted before it reaches your tissues.",
    },
    "exogenous-suppression": {
      stronger:
        "Exogenous testosterone has signaled your body to stop making its own. Your natural production has gone dormant -- that's the dependency you feel. Dormant isn't dead: the pathway restarts when the outside signal eases and the right inputs return.",
      softer:
        "Adding testosterone from outside is quietly telling your body to ease off its own production. The natural pathway is still there -- it just needs the signal to switch back on.",
    },
    // quiz-sleep's forced primary. Deliberately says nothing about sleep DURATION:
    // that quiz's premise is a full night in bed, so the sleep-deprivation framing
    // ("your sleep window is too short") would contradict it. Both tones must read
    // as a problem statement -- buildAssessment picks "softer" for every level below
    // high, so it carries the majority of paths and must never absolve (rule 12a).
    // Length is deliberate: the assessment card is fold-critical and the italic
    // cohort subline sits directly beneath it, so this paragraph must stay ~4 lines
    // at 375px. The first draft ran 198/199 chars (6 rendered lines) against a
    // house median of 109 -- see the sibling entries above; cortisol-dominance, the
    // closest analogue, is 110/87. Do not re-expand it.
    "nocturnal-cortisol": {
      stronger:
        "Your cortisol fires in the early-morning hours, clipping the deep-sleep window that makes your testosterone.",
      softer:
        "Your cortisol is settling later than it should, starting to clip the deep-sleep window that makes your testosterone.",
    },
    // quiz-beer's forced primary. Deliberately makes NO claim about how much the
    // user drinks: "softer" serves every level below high (rule 12a), which
    // includes the man who answered "Barely touch the stuff" -- telling him his
    // beer intake caused this would contradict his own answer. "every beer adds
    // to the load" is true at any dose, including zero. The specific driver is
    // carried by the organic Secondary (AROMATASE LOAD on the belly paths).
    // Length matches the house median: 117/116 chars against cortisol-dominance's
    // 110/87. Both tones must read as a problem -- do not soften either.
    "estrogen-dominance": {
      stronger:
        "Your hormone balance has tipped toward estrogen. Beer's plant estrogens add to a load your body already carries.",
      softer:
        "Your hormone balance is starting to tip toward estrogen, and every beer adds to the load your body already carries.",
    },
  },
  fallbackExplanation: {
    stronger: "Your responses point to a hormonal pattern that needs attention.",
    softer: "Your responses suggest your testosterone signaling is starting to drift.",
  },
};
